import { translate } from '@jsverse/transloco';

import { CrosstabWidgetOptions } from 'app/modules/widget/components/widget-crosstab/widget-crosstab.component';
import { Comment, Widget } from 'app/providers/bridge-api/dashboard.service';
import { Report } from 'app/providers/storage-api/report.service';
import { FilterUtils } from 'app/utils/filter.utils';
import { Utils } from 'app/utils/utils';
import { WidgetDataSource } from '../../../../../models';
import { AbstractChartReport } from '../chart-report-abstract/chart-report-abstract';

export const COMMENT_POINT_ID_SEPARATOR = '|x|';

export interface CrosstabDataItem {
  [CrosstabChartReport.KEY]: string;
  [key: string]: number;
}

export interface CrosstabData {
  columns: string[];
  data: CrosstabDataItem[];
  taxonomyData: {};
  maxValue: number;
  minValue: number;
  totalHits: number;
  documentCounts: { [key: string]: { [key: string]: number } };
}

export class CrosstabChartReport extends AbstractChartReport {
  public static KEY = '__key__';

  reportName = 'crosstabs';

  private graphTypes = ['crosstab', 'dependencywheel'];

  private chartSettings = [
    {
      groupLabel: translate('widgets.options.display.title'),
      groupId: 'chart-settings-display',
      property: 'display',
      needRefresh: true,
      groupValidator: (graphType) => graphType === 'dependencywheel',
      shouldDisplayOptions: (settings, data) =>
        settings.filter((s) => s.shouldDisplay?.(data)).length > 0,
      settings: [
        {
          label: translate('widgets.options.display.heatmap-coloring'),
          property: 'heatmap',
          type: 'checkbox',
          group: true,
          shouldDisplay: (data) => data.payload.graphType !== 'dependencywheel',
        },
        {
          label: translate('widgets.options.display.show-as'),
          property: 'showAsPercentage',
          type: 'toggle',
          leftText: translate('widgets.options.display.absolute'),
          rightText: translate('widgets.options.display.percentage'),
          group: false,
          shouldDisplay: (data) => data.payload.graphType !== 'dependencywheel',
        },
        {
          label: translate('widgets.options.display.decimals-to-display'),
          property: 'decimalPrecision',
          type: 'singleSelect',
          group: false,
          options: ['0', '1', '2'],
          condensedDisplay: true,
          shouldDisplay: (data) => data.payload.graphType !== 'dependencywheel',
          isDisabled: (data) => !data.payload.display.showAsPercentage,
        },
      ],
    },
  ];

  skippedValues = 0;

  reportHandler = (_dashboard, widget, response, _options) => {
    const hasCustomSet =
      (widget.payload.selectedItems && widget.payload.selectedItems.length > 0) ||
      (widget.payload.columnSelectedItems && widget.payload.columnSelectedItems.length > 0);
    let singleItem = false;

    if (!hasCustomSet) {
      singleItem =
        !widget.payload.columnAnalyticItem ||
        (widget.payload.analyticItem === widget.payload.columnAnalyticItem &&
          widget.payload.entityTypeFilter === widget.payload.columnEntityTypeFilter);
    }

    this.skippedValues = 0;
    const dataEntries = [];

    response.data?.forEach((row, rowIndex) => {
      const rowKey = row[CrosstabChartReport.KEY];

      let rowAdded = false;

      response.columns.forEach((col, colIndex) => {
        const id = `${rowKey}${COMMENT_POINT_ID_SEPARATOR}${col}`;

        if (col !== CrosstabChartReport.KEY && row[col] !== 0) {
          if (singleItem) {
            if (colIndex - 1 > rowIndex) {
              dataEntries.push([id, rowKey, col, row[col], row[row[CrosstabChartReport.KEY]]]);
              rowAdded = true;
            }
          } else {
            dataEntries.push([id, rowKey, col, row[col]]);
            rowAdded = true;
          }
        }
      });

      if (!rowAdded) {
        // we can get here if a row has no co-location with any other column. This can happen:
        // - in an A x A chart
        // - in a custom chart when an item has no co-location with any other items
        // normally, we skip these values, we add them back to show the rims on the chart
        // (we can only do this for the A x A case only)
        if (row[rowKey]) {
          dataEntries.push([rowKey, rowKey, rowKey, row[rowKey], row[rowKey]]);
        } else {
          this.skippedValues++;
        }
      }
    });

    response.columns
      .filter((col) => col !== CrosstabChartReport.KEY)
      .forEach((col) => {
        if (!dataEntries.some((e) => e[1] === col || (singleItem && e[0] === col))) {
          this.skippedValues++;
        }
      });

    const formatterFn = this.formatMetadataValue;
    const formatPercentFn = (weight: number, total: number) => {
      return `(${((weight * 100) / total).toFixed(2)}%)`;
    };
    const [rowMetadataType, columnMetadataType] = this.metadataType
      ? this.metadataType.split('|')
      : ['', ''];

    return {
      series: [
        {
          keys: ['id', 'from', 'to', 'weight', 'custom.selfWeight'],
          data: dataEntries,
          type: 'dependencywheel',
          dataLabels: {
            color: '#333',
            crop: true,
            textPath: {
              enabled: false,
              attributes: {
                dy: 5,
              },
            },
            distance: 10,
            nodeFormatter() {
              let nodeName = this.point.name;

              if (this.point.linksFrom.length) {
                nodeName = formatterFn(rowMetadataType, nodeName);
              }
              if (this.point.linksTo.length) {
                nodeName = formatterFn(columnMetadataType, nodeName);
              }
              if (response.taxonomyData && response.taxonomyData[this.point.name]) {
                nodeName = response.taxonomyData[this.point.name].name;
              }
              return Utils.truncate(nodeName);
            },
          },
          size: '95%',
        },
      ],
      tooltip: {
        headerFormat: '',
        nodeFormatter() {
          const selfNode = this.linksFrom[0] || this.linksTo[0];
          const selfWeight = selfNode && selfNode.custom && selfNode.custom.selfWeight;

          let displayName = this.name;

          if (this.linksFrom[0]) {
            displayName = formatterFn(rowMetadataType, this.name);
          }
          if (this.linksTo[0]) {
            displayName = formatterFn(columnMetadataType, this.name);
          }

          if (response.taxonomyData && response.taxonomyData[this.name]) {
            displayName = response.taxonomyData[this.name].name;
          }

          if (selfWeight) {
            return `${displayName}: <b>${Utils.addThousandSeparator(selfWeight)} ${formatPercentFn(
              selfWeight,
              response.totalHits
            )}</b><br/>`;
          }

          return `${displayName}`;
        },
        pointFormatter() {
          let fromNodeName = formatterFn(rowMetadataType, this.fromNode.name);

          if (response.taxonomyData && response.taxonomyData[fromNodeName]) {
            fromNodeName = response.taxonomyData[fromNodeName].name;
          }

          let toNodeName = formatterFn(columnMetadataType, this.toNode.name);

          if (response.taxonomyData && response.taxonomyData[toNodeName]) {
            toNodeName = response.taxonomyData[toNodeName].name;
          }

          if (fromNodeName === toNodeName) {
            return `${fromNodeName}: <b>${Utils.addThousandSeparator(
              this.weight
            )} ${formatPercentFn(this.weight, response.totalHits)}</b><br/>`;
          }

          return `${fromNodeName} × ${toNodeName}: <b>${Utils.addThousandSeparator(
            this.weight
          )} ${formatPercentFn(this.weight, response.totalHits)}
          </b><br/>`;
        },
      },
    };
  };

  getFields() {
    return ['analyticItem', 'limit', 'graphType'];
  }

  getGraphTypes() {
    return this.graphTypes;
  }

  getOptionalFields() {
    return [];
  }

  public getChartSettings(): any[] {
    return this.chartSettings;
  }

  isAnalyticItemSupported(analyticItem) {
    return analyticItem !== 'documents';
  }

  public canHideItems(): boolean {
    return false;
  }

  public canSetChartMaximum(): boolean {
    return false;
  }

  getCommentsForPoint(_point: any, _comments: Comment[]): Comment[] {
    return [];
    // TODO this shall be added once we support comments for both Dependency wheel and Crosstab views;
    // return comments.filter((c) => c.target.pointId === (point.id ?? point.pointId));
  }

  protected getPointForComment(comment: Comment, series: any) {
    if (this.options.graphType === 'crosstab') {
      const [rowId, columnId] = comment.target?.pointId?.split(COMMENT_POINT_ID_SEPARATOR);

      if (rowId && columnId) {
        const value = series.find((s) => s[CrosstabChartReport.KEY] === rowId)?.[columnId] ?? 0;

        return {
          x: value,
          y: value,
        };
      }
    }
    // return series.data?.find((d) => comment.target.pointId === d[0]);
  }

  isCommentVisible(comment: Comment, _widget: Widget, series) {
    if (!series || this.options.graphType !== 'crosstab') {
      return false;
    }

    const [rowId, columnId] = comment.target?.pointId?.split(COMMENT_POINT_ID_SEPARATOR);

    if (rowId && columnId) {
      return series.find((s) => s[CrosstabChartReport.KEY] === rowId)?.[columnId] !== undefined;
    }

    if (rowId) {
      return series.find((s) => s[CrosstabChartReport.KEY] === rowId) !== undefined;
    }

    if (columnId) {
      return series.find((s) => columnId in s);
    }

    return false;
  }

  public async executeReportForCrosstab(
    filterQuery: string,
    payload: CrosstabWidgetOptions,
    widgetDataSource: WidgetDataSource,
    format: 'json' | 'csv' = 'json',
    columnsSize?: number,
    rowsSize?: number,
    _columnsIncludes?: string[],
    _rowsIncludes?: string[],
    _columnsPrefix?: string,
    _rowsPrefix?: string
  ): Promise<CrosstabData> {
    const params = await this.getParams();

    if (payload.customFieldSelected) {
      params.rowsType = `metadata.${payload.analyticItem}`;
    } else if (payload.sectionPropertySelected) {
      // Convert sectionProperties.{field} to flatSections.{field}
      params.rowsType = payload.analyticItem.replace('sectionProperties.', 'flatSections.');
    } else {
      params.rowsType = payload.analyticItem;

      if (payload.entityTypeFilter) {
        params.rowsType += `:${payload.entityTypeFilter}`;
      }
    }

    if (payload.rowSelectedSections) {
      params.rowsSelectedSections = FilterUtils.convertSelectedSectionToFeatureSectionFilter(
        payload.rowSelectedSections
      );
    }

    if (payload.columnCustomFieldSelected) {
      params.columnsType = `metadata.${payload.columnAnalyticItem || payload.analyticItem}`;
    } else if (payload.columnSectionPropertySelected) {
      // Convert sectionProperties.{field} to flatSections.{field}
      params.columnsType = (payload.columnAnalyticItem || payload.analyticItem).replace(
        'sectionProperties.',
        'flatSections.'
      );
    } else {
      params.columnsType = payload.columnAnalyticItem || payload.analyticItem;

      if (payload.columnEntityTypeFilter) {
        params.columnsType += `:${payload.columnEntityTypeFilter}`;
      }
    }

    if (payload.columnSelectedSections) {
      params.columnsSelectedSections = FilterUtils.convertSelectedSectionToFeatureSectionFilter(
        payload.columnSelectedSections
      );
    }

    params.columnsSize = columnsSize || this.options.limit;
    params.rowsSize = rowsSize || this.options.limit;
    params.analysisName = widgetDataSource.analysisId;
    if (payload.excludedValues) {
      const columnExcludes = payload.excludedValues
        .filter((item) => item.axis === 'column' || item.axis === 'all')
        .map((item) => item.name);
      const rowExcludes = payload.excludedValues
        .filter((item) => item.axis === 'row' || item.axis === 'all')
        .map((item) => item.name);

      params.columnsExcludes = JSON.stringify(columnExcludes);
      params.rowsExcludes = JSON.stringify(rowExcludes);
    }

    if (payload.selectedItems && payload.selectedItems.length) {
      params.rowsIncludes = JSON.stringify(payload.selectedItems);
    }

    if (payload.columnSelectedItems && payload.columnSelectedItems.length) {
      params.columnsIncludes = JSON.stringify(payload.columnSelectedItems);
    }

    delete params.excludedValues;
    delete params.topN;
    delete params.featureName;
    delete params.entityTypeFilter;
    delete params.sentimentScale;
    delete params.customField;
    delete params.standardField;
    delete params.selectedItems;

    const response = (await this.reportService.getReport(
      widgetDataSource.collectionName,
      widgetDataSource.groupId,
      this.reportName,
      params,
      format,
      filterQuery,
      this.filterService.buildFilterStringClearPolarityFilters(
        this.options.filters,
        this.options.analyticItem
      ),
      undefined,
      this.options.projectId
    )) as Report<{ [p: string]: any }>;

    this.lastDocumentCount = response.totalHits;

    return this.processResponse(response);
  }

  public async processResponse(response, _filterQuery?: string): Promise<CrosstabData> {
    const columns = [];
    const data = [];
    const taxonomyData = response.extra ? response.extra : {};
    const totalHits = response.totalHits;
    const report = response.items;
    const documentCounts = JSON.parse(JSON.stringify(response.items));
    const options = this.options as CrosstabWidgetOptions;
    if (options.display?.showAsPercentage) {
      for (const row of Object.keys(report)) {
        for (const col of Object.keys(report[row])) {
          report[row][col] = report[row][col] / totalHits;
        }
      }
    }

    if (!Object.keys(report).length) {
      return { columns, data, maxValue: 0, minValue: 0, taxonomyData, totalHits, documentCounts };
    }

    let flattenedData = [];

    const columnHeaders = new Set<string>();
    const rowHeaders = new Set<string>();

    // Here we flatten the data into array of objects
    // to be able to sort them. This will result in a descending order
    // from top-right to bottom-left
    for (const row of Object.keys(report)) {
      rowHeaders.add(row);
      for (const col of Object.keys(report[row])) {
        flattenedData.push({ row, col, value: report[row][col] });
        columnHeaders.add(col);
      }
    }

    if (!flattenedData.length) {
      rowHeaders.forEach((key) =>
        data.push({
          [CrosstabChartReport.KEY]: key,
        })
      );
      return { columns, data, maxValue: 0, minValue: 0, taxonomyData, totalHits, documentCounts };
    }

    flattenedData.sort((d1, d2) => {
      let ret = d2.value - d1.value;

      if (!ret) {
        ret = d1.row.localeCompare(d2.row);

        if (!ret) {
          ret = d1.col.localeCompare(d2.col);
        }
      }

      return ret;
    });

    const maxValue = flattenedData[0].value;
    const minValue = flattenedData[flattenedData.length - 1].value;

    // We traverse the flattened and sorted data here.
    // The first element in every iteration will be at the top-leftmost position.
    // Then we remove the given row and column so we will always have the next
    // cell value available as the first element.
    while (flattenedData.length) {
      const col = flattenedData[0].col;
      const row = flattenedData[0].row;

      columns.push(col);
      data.push({
        [CrosstabChartReport.KEY]: row,
        ...report[row],
      });

      flattenedData = flattenedData.filter((d) => d.row !== row && d.col !== col);
      rowHeaders.delete(row);
      columnHeaders.delete(col);
    }

    // If the original data dimensions did not match, we have to add the missing data here
    // as they were previously removed during sorting.
    // For rows, we have to add the extra row(s) as we already added the column
    rowHeaders.forEach((key) =>
      data.push({
        [CrosstabChartReport.KEY]: key,
        ...report[key],
      })
    );

    // For columns, we just have to add the column header as we already added the rows
    columnHeaders.forEach((key) => columns.push(key));

    if (columns.length) {
      columns.unshift(CrosstabChartReport.KEY);
    }

    return { columns, data, maxValue, minValue, taxonomyData, totalHits, documentCounts };
  }
}
